import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quels types de pannes de fenêtre réparez-vous à Lyon ?",
    a: "Nous intervenons sur tous les types de pannes de fenêtre à Lyon : crémone cassée ou grippée, poignée qui tourne dans le vide, fenêtre bloquée ou qui ferme mal, joint d'étanchéité usé, mécanisme oscillo-battant déréglé, gond fatigué. Nous réparons les fenêtres PVC, aluminium et bois.",
  },
  {
    q: "Intervenez-vous sur les fenêtres de toit type Velux ?",
    a: "Oui, nous intervenons sur les fenêtres de toit Velux et autres marques. Nous assurons le changement de vitrage, le remplacement des joints, la réparation de la barre de manœuvre et le réglage de l'ouverture. Nos interventions sont garanties 10 ans.",
  },
  {
    q: "Combien coûte la réparation d'une fenêtre à Lyon ?",
    a: "Le coût dépend de la nature de la panne et des pièces à remplacer. Nous vous fournissons un devis gratuit et détaillé avant toute intervention. Nos prix sont transparents et compétitifs, sans mauvaise surprise. Contactez-nous pour un diagnostic.",
  },
];

export default function FenetrePilier() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const pageUrl = 'https://depanfenetres-lyon.fr/reparation-fenetre-lyon/';
  const title = "Réparation de fenêtre à Lyon | Dépannage 7j/7 | DFPRO";

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Réparation de fenêtre à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', '@id': 'https://depanfenetres-lyon.fr/#business' },
    areaServed: [{ '@type': 'City', 'name': 'Lyon' }, { '@type': 'AdministrativeArea', 'name': 'Métropole de Lyon' }],
    serviceType: 'Réparation de fenêtre',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', 'name': item.q, 'acceptedAnswer': { '@type': 'Answer', 'text': item.a },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', 'position': 1, 'name': 'Accueil', 'item': 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Réparation de fenêtre à Lyon', 'item': pageUrl },
    ],
  };

  return (
    <>
      <SEOHead
        title={title}
        description="Fenêtre qui ferme mal, bloquée, crémone ou poignée cassée ? Réparation de fenêtre à Lyon, 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Lyon."
        canonicalUrl={pageUrl}
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}></script>

      <Header />

      <main>
        <section className="relative w-full min-h-[350px] md:min-h-[420px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="/images/dfpro-fenetre-pilier-2026.webp"
              alt="Réparation de fenêtre à Lyon par DFPRO Lyon — technicien intervenant sur une fenêtre PVC"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/55 via-foreground-900/35 to-foreground-900/55"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-background-50 leading-tight">
                Réparation de fenêtre à Lyon : PVC, alu et bois
              </h1>
              <p className="mt-4 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre fenêtre ne ferme plus correctement, la crémone est grippée ou la poignée tourne dans le vide ? Nos techniciens interviennent rapidement à Lyon et dans la métropole pour réparer tous types de fenêtres.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler le 09 80 80 36 84
                </a>
                <a href="/contact/" className="inline-flex items-center gap-2 border-2 border-background-50/60 hover:border-background-50 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-file-list-3-line w-5 h-5 flex items-center justify-center"></i>
                  Devis gratuit
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-3 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs md:text-sm flex-wrap whitespace-nowrap" aria-label="Fil d'ariane">
              <a href="/" className="text-secondary-500 hover:text-primary-500 transition-colors">Accueil</a>
              <span className="text-secondary-400">/</span>
              <span className="text-foreground-700 font-medium">Réparation de fenêtre à Lyon</span>
            </nav>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-800">Votre fenêtre a besoin d'une réparation ?</h2>
                <p className="mt-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  Une fenêtre qui ferme mal, c'est une source d'inconfort au quotidien : courants d'air, insécurité, déperdition de chaleur. À Lyon, où les amplitudes thermiques peuvent être marquées entre l'hiver et l'été, une fenêtre en bon état est essentielle pour votre confort et vos économies d'énergie. Nous intervenons sur tous les types de fenêtres — PVC, aluminium et bois — dans tout Lyon et la Métropole de Lyon.
                </p>
                <p className="mt-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  Nos techniciens sont formés au diagnostic et à la réparation de tous les mécanismes de fenêtre : crémone, poignée, compas oscillo-battant, paumelles, joints d'étanchéité. Nous intervenons avec les pièces détachées adaptées à votre menuiserie, quelle que soit la marque ou l'ancienneté. Le dépannage est suivi d'un réglage complet pour garantir une fermeture parfaite et durable.
                </p>

                <h3 className="mt-8 font-heading font-semibold text-base md:text-lg text-foreground-800">Pannes courantes de fenêtre</h3>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Fenêtre bloquée ou qui ferme mal', href: '/fenetre-bloquee-lyon/' },
                    { label: 'Crémone cassée ou grippée', href: '/changer-cremone-fenetre-lyon/' },
                    { label: 'Poignée cassée ou qui tourne dans le vide', href: '/changer-poignee-fenetre-lyon/' },
                    { label: 'Oscillo-battant déréglé', href: '/reglage-oscillo-battant-lyon/' },
                    { label: 'Joint d\'étanchéité usé ou décollé', href: '/changer-joint-fenetre-lyon/' },
                    { label: 'Fenêtre PVC, alu ou bois à rénover', href: '/reparation-fenetre-pvc-alu-bois-lyon/' },
                  ].map((item, idx) => (
                    <a key={idx} href={item.href} className="flex items-center gap-3 bg-background-100 hover:bg-background-200 rounded-lg p-4 transition-colors cursor-pointer no-underline group">
                      <i className="ri-arrow-right-circle-line text-primary-500 w-5 h-5 flex items-center justify-center flex-shrink-0 group-hover:text-accent-500 transition-colors"></i>
                      <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-800">{item.label}</span>
                    </a>
                  ))}
                </div>

                <h3 className="mt-8 font-heading font-semibold text-base md:text-lg text-foreground-800">Types de fenêtres prises en charge</h3>
                <p className="mt-3 text-sm md:text-base text-secondary-500 leading-relaxed">
                  Chaque matériau a ses spécificités et ses pannes propres. Les fenêtres PVC, très répandues dans la région lyonnaise, peuvent présenter des déformations légères avec le temps ou des crémones grippées. Les fenêtres aluminium sont robustes mais leur quincaillerie peut s'user. Les fenêtres bois, plus rares mais présentes dans les immeubles anciens de Lyon, nécessitent un entretien particulier des ferrures et des joints. Quel que soit votre type de fenêtre, nous avons la solution.
                </p>

                <h3 className="mt-8 font-heading font-semibold text-base md:text-lg text-foreground-800">Notre garantie : 10 ans sur toutes les réparations</h3>
                <p className="mt-3 text-sm md:text-base text-secondary-500 leading-relaxed">
                  Toutes nos réparations de fenêtre sont couvertes par une garantie de 10 ans, pièces et main-d'œuvre comprises. C'est un engagement fort qui reflète la qualité de notre travail et la confiance que nous accordons aux pièces que nous utilisons. Une fenêtre bien réparée aujourd'hui, c'est une fenêtre qui fonctionnera parfaitement pendant des années.
                </p>
              </div>

              <aside className="lg:w-72 flex-shrink-0">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h4 className="font-heading font-semibold text-sm text-foreground-800 mb-3">Pannes de fenêtre</h4>
                  <ul className="space-y-2">
                    <li><a href="/fenetre-bloquee-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Fenêtre bloquée</a></li>
                    <li><a href="/changer-cremone-fenetre-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Crémone cassée</a></li>
                    <li><a href="/changer-poignee-fenetre-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Poignée cassée</a></li>
                    <li><a href="/reglage-oscillo-battant-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Oscillo-battant</a></li>
                    <li><a href="/changer-joint-fenetre-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Joint de fenêtre</a></li>
                    <li><a href="/reparation-fenetre-pvc-alu-bois-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">PVC, alu, bois</a></li>
                  </ul>
                  <hr className="my-4 border-background-200" />
                  <h4 className="font-heading font-semibold text-sm text-foreground-800 mb-3">Autres services</h4>
                  <ul className="space-y-2">
                    <li><a href="/reparation-volet-roulant-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Volet roulant</a></li>
                    <li><a href="/vitrier-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Vitrier</a></li>
                    <li><a href="/reparation-baie-vitree-lyon/" className="text-sm text-secondary-500 hover:text-primary-500 transition-colors">Baie vitrée</a></li>
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-lg md:text-2xl text-center text-foreground-800 mb-8">Questions fréquentes sur la réparation de fenêtre</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Votre fenêtre a besoin d'une réparation à Lyon ?"
          subtitle="Appelez-nous au 09 80 80 36 84. Intervention sous 24-48h, devis gratuit, garantie 10 ans."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>

      <Footer />
    </>
  );
}